import { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import PdfViewerContainer from "../../../components/PdfViewerContainer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";

const SELECT = "SELECT";

const personalFields = [
  {
    name: "faactype",
    label: "Account Type",
    type: "select",
    required: true,
    options: ["SAVING", "CURRENT"],
  },
  {
    name: "facifno",
    label: "CIF Number",
    placeholder: "CIF Number",
    maxLength: 11,
    numeric: true,
  },
  {
    name: "faacno",
    label: "Account Number",
    placeholder: "Account Number",
    maxLength: 11,
    numeric: true,
  },
  {
    name: "faname",
    label: "Applicant Name",
    alphaOnly: true,
    placeholder: "Customer Name",
    maxLength: 72,
    required: true,
  },
  { name: "fadob", label: "Date of Birth", type: "date", required: true },
  {
    name: "fagender",
    label: "Gender",
    type: "select",
    required: true,
    options: ["MALE", "FEMALE"],
  },
  {
    name: "famstatus",
    label: "Marital Status",
    type: "select",
    required: true,
    options: ["MARRIED", "UNMARRIED"],
  },
  {
    name: "faaadhaarno",
    label: "Aadhaar Number",
    placeholder: "Aadhaar No",
    maxLength: 12,
    required: true,
    numeric: true,
  },
  { name: "fapan", label: "PAN Number", placeholder: "PAN No", maxLength: 10, uppercase: true },
  {
    name: "fafname",
    label: "Father's Name",
    alphaOnly: true,
    placeholder: "Father's Name",
    maxLength: 36,
    required: true,
  },
];

const guardianFields = [
  {
    name: "fagname",
    label: "Guardian's Name",
    alphaOnly: true,
    placeholder: "Guardian's Name",
    maxLength: 36,
    required: true,
  },
  {
    name: "fagrelation",
    label: "Relation",
    type: "select",
    required: true,
    options: [
      "FATHER",
      "MOTHER",
      "HUSBAND",
      "WIFE",
      "SON",
      "DAUGHTER",
      "BROTHER",
      "SISTER",
      "OTHER",
    ],
  },
];

const addressFields = [
  {
    name: "faadd1",
    label: "Address Line 1",
    placeholder: "Address Line 1",
    maxLength: 36,
    required: true,
  },
  {
    name: "faadd2",
    label: "Address Line 2",
    placeholder: "Address Line 2",
    maxLength: 36,
    required: true,
  },
  {
    name: "facity",
    label: "City",
    placeholder: "City",
    maxLength: 13,
    required: true,
  },
  {
    name: "fadist",
    label: "District",
    placeholder: "District",
    maxLength: 23,
    required: true,
  },
  {
    name: "fastate",
    label: "State",
    placeholder: "State",
    maxLength: 13,
    required: true,
  },
  {
    name: "fapin",
    label: "PIN",
    placeholder: "PIN",
    maxLength: 6,
    required: true,
    numeric: true,
  },
  {
    name: "fabplace",
    label: "Birth Place",
    placeholder: "Birth Place",
    maxLength: 13,
    required: true,
  },
  {
    name: "famobile",
    label: "Mobile",
    placeholder: "Mobile",
    maxLength: 10,
    required: true,
    numeric: true,
  },
  {
    name: "faincome",
    label: "Annual Income",
    placeholder: "Income",
    maxLength: 8,
    required: true,
    numeric: true,
  },
  {
    name: "fareligion",
    label: "Religion",
    type: "select",
    required: true,
    options: ["HINDU", "MUSLIM", "CHRISTIAN", "SIKH"],
  },
  {
    name: "facategory",
    label: "Category",
    type: "select",
    required: true,
    options: ["GENERAL", "OBC", "SC", "ST"],
  },
  {
    name: "faqualification",
    label: "Qualification",
    type: "select",
    required: true,
    options: [
      ["9TH", "upto 9th Class passed"],
      ["10TH", "10th Class passed"],
      ["GRADUATE", "Graduate (Gen.)"],
      ["PGRADUATE", "Post Graduate(Gen.)"],
      ["MEDGRADUATE", "Med. Graduate/Post Graduate"],
      ["ENGGRADUATE", "Eng. Graduate/Post Graduate"],
      ["LAWGRADUATE", "Law Graduate/Post Graduate"],
      ["CA", "CA/ICWA/MBA/CFA"],
      ["COMPUTER", "Computer Degree/Diploma/MCA"],
      ["OPROFFESIONAL", "Other Professional Degree/Diploma"],
      ["ILLITERATE", "Illiterate"],
    ],
  },
  {
    name: "faoccupation",
    label: "Occupation",
    type: "select",
    required: true,
    options: [
      ["STATE_GOVT", "State Govt."],
      ["CENTRAL_GOVT", "Central Govt."],
      ["PUBLIC_SECTOR_U", "Public Sector Undertaking"],
      ["DEFENCE", "Defence"],
      ["PRIVATE_SECTOR", "Private Sector"],
      ["INDUSTRIALIST", "Industrialist"],
      ["TRADE_SECTOR", "Trade Sector"],
      ["SERVICE_SECTOR", "Service Sector"],
      ["MIGRANT_LABOUR", "Migrant Labour"],
      ["CONTRACTOR", "Contractor"],
      ["JEWELLER", "Jeweller / Bullion Trader"],
      ["PAWN_SHOP", "Pawn Shop"],
      ["IMPORT_EXPORT", "Import / Export Customer"],
      ["OTHER_SELF_EMPLOYER", "Other Self Employed"],
      ["MEDICAL_PROFFESIONAL", "Medical Professional"],
      ["LEGAL_PROFEESIONAL", "Legal Professional"],
      ["CA_ICWA", "CA/ICWA/Taxation/Finance"],
      ["ENGINEER", "Eng./Architect/Tech. Consultant"],
      ["RETIRED", "Retired"],
      ["JOURNALIST", "Journalist"],
      ["HOUSEWIFE", "Housewife"],
      ["STUDENT", "Student"],
      ["SHARE", "Share and Stock Broker"],
      ["OTHER_PROFFESIONAL", "Other Professional"],
      ["AGRICULTURE", "Agriculture"],
      ["POLITICAL", "Political / Social Worker"],
      ["NOT_CATEGORISED", "Not categorised"],
    ],
  },
];

const nomineeFields = [
  {
    name: "nname",
    label: "Nominee Name",
    alphaOnly: true,
    placeholder: "Nominee Name",
    maxLength: 40,
    required: true,
  },
  {
    name: "nrelation",
    label: "Relation",
    type: "select",
    required: true,
    options: [
      "FATHER",
      "MOTHER",
      "HUSBAND",
      "WIFE",
      "SON",
      "DAUGHTER",
      "BROTHER",
      "SISTER",
      "OTHER",
    ],
  },
  { name: "ndob", label: "Date of Birth", type: "date", required: true },
  {
    name: "ngname",
    label: "Guardian's Name",
    alphaOnly: true,
    placeholder: "Guardian's Name",
    maxLength: 33,
    required: true,
  },
  {
    name: "ngage",
    label: "Guardian's Age",
    placeholder: "Age",
    maxLength: 2,
    required: true,
    numeric: true,
  },
];

const optionalFields = [
  {
    name: "atm",
    label: "ATM",
    type: "select",
    required: true,
    options: ["YES", "NO"],
  },
  {
    name: "cbook",
    label: "Cheque Book",
    type: "select",
    required: true,
    options: ["YES", "NO"],
  },
  {
    name: "ibanking",
    label: "Internet Banking",
    type: "select",
    required: true,
    options: ["YES", "NO"],
  },
];

const allFields = [
  ...personalFields,
  ...guardianFields,
  ...addressFields,
  ...nomineeFields,
  ...optionalFields,
];

const initialFormData = allFields.reduce((data, field) => {
  data[field.name] = field.type === "select" ? SELECT : "";
  return data;
}, {});

const tabs = ["Personal", "Address", "Nominee", "Optional Services", "Preview"];

function optionValue(option) {
  return Array.isArray(option) ? option[0] : option;
}

function optionLabel(option) {
  return Array.isArray(option) ? option[1] : option;
}

function formatDate(dateValue) {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return dateValue;
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function getAge(dateValue) {
  if (!dateValue) return 0;
  const dob = new Date(dateValue);
  if (Number.isNaN(dob.getTime())) return 0;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age -= 1;
  }

  return age;
}

function validateField(field, value) {
  const normalizedValue = String(value ?? "").trim();

  if (field.required) {
    if (field.type === "select" && normalizedValue === SELECT) {
      return `${field.label} is required.`;
    }

    if (!normalizedValue) {
      return `${field.label} is required.`;
    }
  }

  if (!normalizedValue || normalizedValue === SELECT) return "";

  if (field.alphaOnly && !/^[A-Za-z ]+$/.test(normalizedValue)) {
    return `${field.label} must contain alphabet letters only.`;
  }

  if (field.name.endsWith("aadhaarno") && !/^\d{12}$/.test(normalizedValue)) {
    return "Aadhaar Number must contain exactly 12 digits.";
  }

  if (field.name.endsWith("pan") && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(normalizedValue)) {
    return "Enter a valid PAN number (e.g. ABCDE1234F).";
  }

  if (field.name.endsWith("pin") && !/^\d{6}$/.test(normalizedValue)) {
    return "PIN must contain exactly 6 digits.";
  }

  return "";
}

export default function Single() {
  const [currentTab, setCurrentTab] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [maxAllowedTab, setMaxAllowedTab] = useState(0);
  const [pdfUrl, setPdfUrl] = useState("");
  const [showPdf, setShowPdf] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const nextButtonRef = useRef(null);


  useEffect(() => {
  const controller = new AbortController();

  const loadAutofill = async () => {
    try {
      const response = await fetch(
        `/backend/api/autofill.php?form=${encodeURIComponent("SBIAOF-S3")}`,
        {
          method: "GET",
          credentials: "include",
          cache: "no-store",
          headers: {
            Accept: "application/json",
          },
          signal: controller.signal,
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Unable to load autofill details.");
      }

      if (!result.found || !result.data) {
        return;
      }

      setFormData((previous) => ({
        ...previous,

        branchname: previous.branchname || result.data.branch || "",
        branchcode: previous.branchcode || result.data.code || "",

        facity: previous.facity || result.data.city || "",
        fadist: previous.fadist || result.data.dist || "",
        fastate: previous.fastate || result.data.state || "",
        fapin: previous.fapin || result.data.pin || "",
      }));
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Autofill error:", error);
      }
    }
  };

  loadAutofill();

  return () => {
    controller.abort();
  };
}, []);


  const applicantIsMinor = useMemo(() => {
    return formData.fadob ? getAge(formData.fadob) < 18 : false;
  }, [formData.fadob]);

  const nomineeIsMinor = useMemo(() => {
    return formData.ndob ? getAge(formData.ndob) < 18 : true;
  }, [formData.ndob]);

  const visibleFieldsForCurrentTab = useMemo(() => {
    if (currentTab === 0) {
      return applicantIsMinor
        ? [...personalFields, ...guardianFields]
        : personalFields;
    }

    if (currentTab === 1) return addressFields;

    if (currentTab === 2) {
      return nomineeIsMinor
        ? nomineeFields
        : nomineeFields.filter(
            (field) => !["ngname", "ngage"].includes(field.name),
          );
    }

    if (currentTab === 3) return optionalFields;

    return [];
  }, [applicantIsMinor, currentTab, nomineeIsMinor]);

  const firstFieldName = visibleFieldsForCurrentTab[0]?.name || "";
  const lastFieldName =
    visibleFieldsForCurrentTab[visibleFieldsForCurrentTab.length - 1]?.name || "";

  useEffect(() => {
    if (!firstFieldName) return;

    const focusFirstField = () => {
      const firstField = document.getElementById(firstFieldName);

      if (firstField) {
        firstField.focus();
      }
    };

    const frameId = window.requestAnimationFrame(focusFirstField);

    return () => window.cancelAnimationFrame(frameId);
  }, [currentTab, firstFieldName]);

  const handleLastFieldKeyDown = (event, fieldName) => {
    if (fieldName !== lastFieldName) return;

    if (event.key === "Tab" && !event.shiftKey) {
      event.preventDefault();
      nextButtonRef.current?.focus();
    }
  };


  const fieldsForValidation = useMemo(() => {
    const pages = [
      applicantIsMinor
        ? [...personalFields, ...guardianFields]
        : personalFields,
      addressFields,
      nomineeIsMinor
        ? nomineeFields
        : nomineeFields.filter(
            (field) => !["ngname", "ngage"].includes(field.name),
          ),
      optionalFields,
    ];
    return pages[currentTab] || [];
  }, [applicantIsMinor, currentTab, nomineeIsMinor]);

  const updateValue = (name, value) => {
    setFormData((prev) => {
      const next = { ...prev, [name]: value };

      if (name === "fadob" && getAge(value) >= 18) {
        next.fagname = "";
        next.fagrelation = SELECT;
      }

      if (name === "ndob" && getAge(value) > 17) {
        next.ngname = "";
        next.ngage = "";
      }

      return next;
    });

    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleChange = (field, event) => {
    let value = event.target.value;

    if (field.numeric) {
      value = value.replace(/\D/g, "");
    }

    if (field.alphaOnly) {
      value = value.replace(/[^A-Za-z ]/g, "");
    }

    if (field.uppercase || field.name.endsWith("pan")) {
      value = value.toUpperCase();
    }

    updateValue(field.name, value);
  };

  const validateCurrentTab = () => {
    const newErrors = {};

    fieldsForValidation.forEach((field) => {
      const message = validateField(field, formData[field.name]);
      if (message) newErrors[field.name] = message;
    });

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const goNext = (event) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (!validateCurrentTab()) return;

    const nextTab = currentTab + 1;

    if (nextTab < tabs.length) {
      setCurrentTab(nextTab);
      setMaxAllowedTab((prev) => Math.max(prev, nextTab));
    }
  };

  const goPrevious = () => {
    if (currentTab > 0) {
      setCurrentTab(currentTab - 1);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    setPdfLoading(true);
    setPdfError("");

    try {
      const postData = new FormData();

      Object.entries(formData).forEach(([name, value]) => {
        postData.append(name, value === SELECT ? "" : String(value ?? ""));
      });

      const response = await fetch("/FillMyForm/SBIAOF3/SingleForm.php", {
        method: "POST",
        body: postData,
        credentials: "include",
        headers: {
          Accept: "application/pdf",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          errorText || `PDF generation failed. HTTP status: ${response.status}`,
        );
      }

      const contentType = response.headers.get("content-type") || "";

      if (!contentType.includes("application/pdf")) {
        const invalidResponse = await response.text();
        console.error("Invalid PDF response:", invalidResponse);
        throw new Error(
          invalidResponse || "The server did not return a valid PDF file.",
        );
      }

      const pdfBlob = await response.blob();
      const objectUrl = URL.createObjectURL(pdfBlob);

      setPdfUrl((previousUrl) => {
        if (previousUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(previousUrl);
        }
        return objectUrl;
      });

      setShowPdf(true);
    } catch (error) {
      console.error("PDF generation error:", error);
      setPdfError(error.message || "Unable to generate PDF preview.");
    } finally {
      setPdfLoading(false);
    }
  };

  const renderField = (field) => {
    const error = errors[field.name];

    return (
      <div key={field.name} className="space-y-2">
        <Label htmlFor={field.name}>
          {field.label}
          {field.required && <span className="text-red-500"> *</span>}
        </Label>

        {field.type === "select" ? (
          <Select
            value={formData[field.name]}
            onValueChange={(value) => updateValue(field.name, value)}
          >
            <SelectTrigger
              id={field.name}
              className={error ? "border-red-500" : ""}
              onKeyDown={(event) =>
                handleLastFieldKeyDown(event, field.name)
              }
            >
              <SelectValue placeholder={`Select ${field.label}`} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SELECT}>SELECT</SelectItem>
              {field.options.map((option) => (
                <SelectItem
                  key={optionValue(option)}
                  value={optionValue(option)}
                >
                  {optionLabel(option)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            id={field.name}
            name={field.name}
            type={field.type || "text"}
            value={formData[field.name]}
            placeholder={field.placeholder || field.label}
            maxLength={field.maxLength}
            onChange={(event) => handleChange(field, event)}
            onKeyDown={(event) =>
              handleLastFieldKeyDown(event, field.name)
            }
            className={error ? "border-red-500" : ""}
            autoComplete="on"
          />
        )}

        {error && <p className="text-sm text-red-500">{error}</p>}
      </div>
    );
  };

  const previewRows = [
    ["Account Type", formData.faactype],
    ["CIF Number", formData.facifno],
    ["Account Number", formData.faacno],
    ["Applicant Name", formData.faname],
    ["Date of Birth", formatDate(formData.fadob)],
    ["Gender", formData.fagender],
    ["Marital Status", formData.famstatus],
    ["Aadhaar Number", formData.faaadhaarno],
    ["PAN Number", formData.fapan],
    ["Father's Name", formData.fafname],
    ["Guardian's Name", formData.fagname],
    ["Guardian's Relation", formData.fagrelation],
    ["Address Line 1", formData.faadd1],
    ["Address Line 2", formData.faadd2],
    ["City", formData.facity],
    ["District", formData.fadist],
    ["State", formData.fastate],
    ["PIN", formData.fapin],
    ["Birth Place", formData.fabplace],
    ["Mobile", formData.famobile],
    ["Income", formData.faincome],
    ["Religion", formData.fareligion],
    ["Category", formData.facategory],
    [
      "Qualification",
      optionLabel(
        addressFields
          .find((f) => f.name === "faqualification")
          .options.find(
            (opt) => optionValue(opt) === formData.faqualification,
          ) || formData.faqualification,
      ),
    ],
    [
      "Occupation",
      optionLabel(
        addressFields
          .find((f) => f.name === "faoccupation")
          .options.find((opt) => optionValue(opt) === formData.faoccupation) ||
          formData.faoccupation,
      ),
    ],
  ];

  const nomineePreviewRows = [
    ["Nominee Name", formData.nname],
    ["Relation", formData.nrelation],
    ["Date of Birth", formatDate(formData.ndob)],
    ["Guardian's Name", formData.ngname],
    ["Guardian's Age", formData.ngage],
    ["ATM", formData.atm],
    ["Cheque Book", formData.cbook],
    ["Internet Banking", formData.ibanking],
  ];

  if (showPdf && pdfUrl) {
    return (
      <PdfViewerContainer
        pdfUrl={pdfUrl}
        title="SBI Single Account Form"
        onBack={() => setShowPdf(false)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-3xl">
              🏦 SBI Account Opening Form
            </CardTitle>
            <p className="text-muted-foreground">
              Fill customer details carefully.
            </p>
          </CardHeader>
        </Card>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
          {tabs.map((tab, index) => (
            <Button
              key={tab}
              type="button"
              variant={currentTab === index ? "default" : "outline"}
              disabled={index > maxAllowedTab}
              onClick={() => setCurrentTab(index)}
            >
              {index + 1}. {tab}
            </Button>
          ))}
        </div>

        <div>
          {currentTab === 0 && (
            <Card>
              <CardHeader>
                <CardTitle>👤 First Applicant Personal Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {personalFields.map(renderField)}
                </div>

                {applicantIsMinor && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">
                      Guardian Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {guardianFields.map(renderField)}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {currentTab === 1 && (
            <Card>
              <CardHeader>
                <CardTitle>🏠 First Applicant Address Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {addressFields.map(renderField)}
                </div>
              </CardContent>
            </Card>
          )}

          {currentTab === 2 && (
            <Card>
              <CardHeader>
                <CardTitle>👥 Nomination Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {(nomineeIsMinor
                    ? nomineeFields
                    : nomineeFields.filter(
                        (field) => !["ngname", "ngage"].includes(field.name),
                      )
                  ).map(renderField)}
                </div>
              </CardContent>
            </Card>
          )}

          {currentTab === 3 && (
            <Card>
              <CardHeader>
                <CardTitle>💳 Optional Services</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {optionalFields.map(renderField)}
                </div>
              </CardContent>
            </Card>
          )}

          {currentTab === 4 && (
            <Card>
              <CardHeader>
                <CardTitle>✅ Preview</CardTitle>
              </CardHeader>
              <CardContent className="space-y-8">
                <PreviewSection
                  title="First Applicant Details"
                  rows={previewRows}
                />
                <PreviewSection
                  title="Nomination Details"
                  rows={nomineePreviewRows}
                />
              </CardContent>
            </Card>
          )}

          {pdfLoading && (
            <div className="mt-6 text-center">
              Generating PDF, please wait...
            </div>
          )}

          {pdfError && (
            <div className="mt-6 rounded-lg border border-red-500 p-4 text-red-500">
              {pdfError}
            </div>
          )}

          <div className="flex justify-between mt-8">
            <Button
              type="button"
              variant="outline"
              onClick={goPrevious}
              disabled={currentTab === 0 || pdfLoading}
            >
              ← Previous
            </Button>

            {currentTab === tabs.length - 1 ? (
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={pdfLoading}
              >
                {pdfLoading ? "Generating PDF..." : "Submit"}
              </Button>
            ) : (
              <Button
                ref={nextButtonRef}
                type="button"
                onClick={goNext}
                disabled={pdfLoading}
              >
                {currentTab === tabs.length - 2 ? "Preview" : "Next →"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewSection({ title, rows }) {
  return (
    <div className="rounded-2xl border p-6">
      <h3 className="text-xl font-semibold text-center mb-6">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {rows.map(([label, value]) => (
          <div key={label} className="grid grid-cols-2 gap-3 border-b pb-2">
            <div className="font-medium text-muted-foreground">{label}</div>
            <div>{value && value !== SELECT ? value : "-"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
